import { useState, useCallback, useEffect, type ReactNode } from 'react'
import {
  type JwtPayload,
  getCurrentUser,
  getToken,
  login as authLogin,
  logout as authLogout,
} from '../lib/auth'
import { apiFetch } from '../lib/api'
import { AuthContext } from './authContextCore'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<JwtPayload | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const existing = getCurrentUser()
    setUser(existing)
    setIsLoading(false)
  }, [])

  const login = useCallback(async (username: string, password: string) => {
    const data = await apiFetch<{ access_token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    const payload = authLogin(data.access_token)
    if (!payload) {
      throw new Error('Invalid token received from server')
    }
    setUser(payload)
  }, [])

  const logout = useCallback(() => {
    // LOGIN TRACKING: tell the backend the session is over so the token
    // cannot be replayed until its 24 h expiry. A plain fetch, not
    // apiFetch — a 401 here (already ended) must not bounce the page —
    // and keepalive so the request survives the navigation that follows.
    const token = getToken()
    if (token) {
      fetch('/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {})
    }
    authLogout()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
